import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { BsTrash } from 'react-icons/bs';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import { CompanyService } from '../CompanyService';
import { Company } from '../../../model/company/Company';
import { getCorrectEnums } from '../../../utils/Functions';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { useDispatch } from 'react-redux';
import generalSlice from '../../../store/general/GeneralSlice';

const CLIENT_TYPE_OPTIONS = ['client', 'client_light', 'client_with_book'];

interface IModalAdd {
    showModal: boolean;
    isClient: boolean | undefined;
    title: string;
    setModalData: (args: any) => void;
    companyId: number;
}
export default function ModalAdd({ showModal, title, isClient, setModalData, companyId }: IModalAdd) {
    const dispatch = useDispatch();
    const [user, setUser] = useState({
        clientId: 0,
        supplierId: 0,
    });
    const [users, setUsers] = useState<{ assignedCompanies: Company[]; notAssignedCompanies: Company[] }>({
        assignedCompanies: [],
        notAssignedCompanies: [],
    });
    const [filterUser, setFilterUser] = useState('');
    const [triggerUsers, setTriggerUser] = useState(false);

    useEffect(() => {
        CompanyService.getCompanyPartners({
            companyId: companyId,
            excludeClients: isClient,
            excludeSuppliers: !isClient,
            isSupplier: !isClient,
            search: filterUser,
            perPage: 15,
        })
            .then(data => {
                setUsers((prev: any) => ({ ...prev, notAssignedCompanies: data.data }));
            })
            .catch(err => ErrorToast(err));
    }, [showModal, companyId, filterUser, triggerUsers]);

    useEffect(() => {
        CompanyService.getCompanyPartnersAssigned({
            companyId: companyId,
            type: !!isClient ? 'supplier' : 'client',
            perPage: 100,
        })
            .then(data => {
                setUsers((prev: any) => ({ ...prev, assignedCompanies: data.data }));
            })
            .catch(err => ErrorToast(err));
    }, [showModal, companyId, triggerUsers]);

    useEffect(() => {
        if (!!user.clientId || !!user?.supplierId) {
            dispatch(
                generalSlice.actions.setConfirmModal({
                    show: true,
                    contentBody: `Are you sure you want to add ${isClient ? 'client' : 'supplier'}?`,
                    action: postCompanyPartner,
                })
            );
        }
    }, [user]);

    const postCompanyPartner = () => {
        if (!!user.clientId || !!user?.supplierId) {
            CompanyService.postCompanyPartner({
                clientId: !!isClient ? user.clientId : companyId,
                supplierId: !!isClient ? companyId : user?.supplierId,
            })
                .then(() => {
                    setFilterUser('');
                    setTriggerUser(!triggerUsers);
                    dispatch(
                        generalSlice.actions.setConfirmModal({
                            show: false,
                        })
                    );
                })
                .catch(err => ErrorToast(err));
        }
    };

    const removePartner = (clientId: number, supplierId: number) => {
        CompanyService.deleteCompanyPartner(clientId, supplierId)
            .then(() => {
                setTriggerUser(!triggerUsers);
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
            })
            .catch(err => ErrorToast(err));
    };

    const toggleClientLight = (relationId: number, clientType: string) => {
        CompanyService.toggleClientLight(relationId, { clientType: clientType || 'client' })
            .then(() => {
                setTriggerUser(!triggerUsers);
                SuccessToast('Successfully action');
            })
            .catch(err => ErrorToast(err));
    };

    return (
        <Modal
            animation={false}
            show={showModal}
            onHide={() => {
                setModalData((prev: any) => ({ ...prev, showModal: false }));
                setUsers({ assignedCompanies: [], notAssignedCompanies: [] });
            }}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
        >
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">Add {!!isClient ? 'Client' : 'Supplier'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    Choose {!!isClient ? 'client' : 'supplier'} for {title} company?
                </div>
                <div className="form-group col-md-6 col-sm-12 p-0 mt-2 mb-2">
                    <div className="autocomplete-input zindex-5 position-relative">
                        <AutocompleteInput
                            placeholder={'Company display name or master email'}
                            onSelect={setUser}
                            setFilter={setFilterUser}
                            keyState={!!isClient ? 'clientId' : 'supplierId'}
                            keyItemSelect={'id'}
                            notRememberFilter
                            value={filterUser}
                            items={users?.notAssignedCompanies}
                            renderItems={['id', 'displayName']}
                            classes="zindex-5"
                        />
                    </div>
                </div>
                <div className="scrollable-div">
                    {users?.assignedCompanies?.map((company: Company) => {
                        const brandMaster = company?.companyUsers?.find(u => u.userType === 'brand_master');
                        const thumbnail = company?.profilePhoto?.thumbnail || '/images/placeholder.png';

                        return (
                            <div key={company?.id} className="d-flex border border-primary p-2 rounded mb-4">
                                <div className="d-flex align-items-center">
                                    <div className="mr-2">
                                        <img
                                            alt="Company Logo"
                                            className="img-cover"
                                            src={thumbnail}
                                            style={{ width: 35, height: 35 }}
                                        />
                                    </div>
                                    <div className="d-flex flex-column">
                                        <p className="mb-0 font-14 fw-bolder">{company?.displayName}</p>
                                        <p className="mb-0 font-12">
                                            <b>
                                                {brandMaster?.firstName} {brandMaster?.lastName}
                                            </b>{' '}
                                            {brandMaster?.email}
                                        </p>
                                    </div>
                                </div>
                                <div className="col d-flex align-items-center justify-content-end">
                                    {!!isClient && (
                                        <Form.Select
                                            className="ml-2"
                                            style={{ width: 160 }}
                                            value={company?.clientRelations?.[0]?.clientType || 'client'}
                                            onChange={e => {
                                                toggleClientLight(company?.clientRelations?.[0]?.id, e.target.value);
                                            }}
                                        >
                                            {CLIENT_TYPE_OPTIONS.map(option => (
                                                <option key={option} value={option}>
                                                    {getCorrectEnums(option)}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    )}
                                    <Button
                                        className="ml-2"
                                        variant="danger"
                                        onClick={() => {
                                            dispatch(
                                                generalSlice.actions.setConfirmModal({
                                                    show: true,
                                                    contentBody: `Are you sure you want to delete ${
                                                        isClient ? 'client' : 'supplier'
                                                    }?`,
                                                    action: () =>
                                                        removePartner(
                                                            !!isClient ? company.id : companyId,
                                                            !!isClient ? companyId : company.id
                                                        ),
                                                })
                                            );
                                        }}
                                    >
                                        <BsTrash />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-primary"
                    onClick={() => {
                        setModalData((prev: any) => ({ ...prev, showModal: false }));
                        setUsers({ assignedCompanies: [], notAssignedCompanies: [] });
                    }}
                >
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
